import { message } from 'antd';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import { AssessmentApiError, getAssessment } from '@/api/assessments/api';
import { fetchKnowledgeNoteCard } from '@/api/knowledge/api';
import {
  analysisOptionsFromDTO,
  EMPTY_ANALYSIS_OPTIONS,
  type AnalysisOptions,
  type Step1DocCard,
} from '@/models/assessment.model';

export interface UseAssessmentDataOptions {
  assessmentInstanceId: string;
  accessToken: string | null;
  clientId: string;
  projectId: string;
}

export interface UseAssessmentDataResult {
  isLoading: boolean;
  isStep1Approved: boolean;
  transcriptCardIds: string[];
  clientDocCardIds: string[];
  setTranscriptCardIds: Dispatch<SetStateAction<string[]>>;
  setClientDocCardIds: Dispatch<SetStateAction<string[]>>;
  analysisOptions: AnalysisOptions;
  setAnalysisOptions: Dispatch<SetStateAction<AnalysisOptions>>;
  engagementContext: string | null;
  stakeholderListMarkdown: string | null;
  step1DocCards: Step1DocCard[];
}

/**
 * Fetch assessment data on mount and manage the card ID arrays that other handlers mutate.
 * Owns: isLoading, isStep1Approved, transcriptCardIds, clientDocCardIds, analysisOptions,
 * engagementContext, stakeholderListMarkdown, step1DocCards.
 */
export function useAssessmentData({
  assessmentInstanceId,
  accessToken,
  clientId,
  projectId,
}: UseAssessmentDataOptions): UseAssessmentDataResult {
  const [isLoading, setIsLoading] = useState(true);
  const [isStep1Approved, setIsStep1Approved] = useState(false);
  const [transcriptCardIds, setTranscriptCardIds] = useState<string[]>([]);
  const [clientDocCardIds, setClientDocCardIds] = useState<string[]>([]);
  const [analysisOptions, setAnalysisOptions] = useState<AnalysisOptions>(EMPTY_ANALYSIS_OPTIONS);
  const [engagementContext, setEngagementContext] = useState<string | null>(null);
  const [stakeholderListMarkdown, setStakeholderListMarkdown] = useState<string | null>(null);
  const [step1DocCardIds, setStep1DocCardIds] = useState<string[]>([]);
  const [step1DocCards, setStep1DocCards] = useState<Step1DocCard[]>([]);

  useEffect(() => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getAssessment(assessmentInstanceId, accessToken, clientId, projectId)
      .then((dto) => {
        if (cancelled) {
          return;
        }
        setTranscriptCardIds(dto.interview_transcript_card_ids ?? []);
        setClientDocCardIds(dto.client_document_card_ids ?? []);
        setAnalysisOptions(analysisOptionsFromDTO(dto.analysis_options));
        setEngagementContext(dto.engagement_context ?? null);
        setStakeholderListMarkdown(dto.stakeholder_list_markdown ?? null);
        setStep1DocCardIds(dto.supporting_doc_card_ids ?? []);
        setIsStep1Approved(Boolean(dto.interview_guide_approved_at));
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        if (err instanceof AssessmentApiError) {
          void message.error('Failed to load assessment data.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, assessmentInstanceId, clientId, projectId]);

  useEffect(() => {
    if (!accessToken || step1DocCardIds.length === 0) {
      setStep1DocCards([]);
      return;
    }
    let cancelled = false;
    void Promise.all(
      step1DocCardIds.map(async (id) => {
        try {
          const result = await fetchKnowledgeNoteCard(accessToken, clientId, id, projectId);
          return result.found && result.card
            ? { cardId: id, title: result.card.title }
            : { cardId: id, title: 'Document' };
        } catch {
          return { cardId: id, title: 'Document' };
        }
      }),
    ).then((cards) => {
      if (!cancelled) {
        setStep1DocCards(cards);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [step1DocCardIds, accessToken, clientId, projectId]);

  return {
    isLoading,
    isStep1Approved,
    transcriptCardIds,
    clientDocCardIds,
    setTranscriptCardIds,
    setClientDocCardIds,
    analysisOptions,
    setAnalysisOptions,
    engagementContext,
    stakeholderListMarkdown,
    step1DocCards,
  };
}
